import {
  DeleteOutlined,
  EditOutlined,
  MenuOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Flex, List, Popover, Space } from 'antd'
import { DefaultRenderConfigPanel } from 'features/drilldown/components/table/columnRenders/TableDefaultColumnRender'
import { useOntology } from 'features/dynamicOntology'
import first from 'lodash/first'
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'

import { useDrilldownTableConfig } from './DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from './DrilldownTableSchema'

interface ColumnListItemProps {
  column: DrilldownTableColumnDef
  index: number
  isEditing: boolean
  onEdit?: () => void
  onDelete?: () => void
  onDrop: (dragIndex: number, dropIndex: number) => void
  onUpdateColumn?: Dispatch<SetStateAction<DrilldownTableColumnDef>>
  onPopoverVisibleChange: (visible: boolean) => void
}

const ColumnListItem: FC<ColumnListItemProps> = ({
  column,
  index,
  isEditing,
  onEdit,
  onDelete,
  onDrop,
  onUpdateColumn,
  onPopoverVisibleChange,
}) => (
  <List.Item
    draggable
    style={{ cursor: 'grab' }}
    onDragStart={(event) => {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(index))
    }}
    onDragOver={(event) => {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    }}
    onDrop={(event) => {
      event.preventDefault()
      const dragIndex = Number(event.dataTransfer.getData('text/plain'))
      if (!Number.isNaN(dragIndex)) {
        onDrop(dragIndex, index)
      }
    }}
    actions={[
      ...(onEdit && onUpdateColumn
        ? [
            <Popover
              key='edit'
              content={
                <DefaultRenderConfigPanel
                  columnDef={column}
                  setColumnDef={onUpdateColumn}
                />
              }
              title='Edit Column'
              trigger='click'
              open={isEditing}
              onOpenChange={(visible) => onPopoverVisibleChange(visible)}>
              <Button icon={<EditOutlined />} onClick={() => onEdit()} />
            </Popover>,
          ]
        : []),
      ...(onDelete
        ? [
            <Button
              key='delete'
              icon={<DeleteOutlined />}
              danger
              onClick={() => onDelete()}
            />,
          ]
        : []),
    ]}>
    <Space>
      <MenuOutlined />
      {column.title}
    </Space>
  </List.Item>
)

export const DrilldownTableColumnListEditor: React.FC = () => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false)
  const { columnDefs, setColumnDefs, groupByType } = useDrilldownTableConfig()

  const { fields, isDescendant } = useOntology()
  const defaultColumn = useMemo<DrilldownTableColumnDef | undefined>(() => {
    const allowedEntries = Object.entries(fields).filter(([, def]) => {
      const kType = def?.entryType
      return (
        !!kType &&
        (isDescendant(kType, groupByType) || isDescendant(groupByType, kType))
      )
    })
    const [field, fieldDef] =
      allowedEntries.find(([, def]) => def.entryType === groupByType) ??
      first(allowedEntries) ??
      []
    if (!field || !fieldDef) {
      return undefined
    }
    return {
      field,
      title: fieldDef.name,
    }
  }, [fields, groupByType, isDescendant])

  const handleAddColumn = useCallback(() => {
    if (!defaultColumn) {
      return
    }
    setColumnDefs?.((prevColumns) => [
      ...(prevColumns ?? []),
      { ...defaultColumn },
    ])
    setEditingIndex(columnDefs.length) // Set editing index to the new column
    setPopoverVisible(true) // Open popover immediately
  }, [columnDefs.length, defaultColumn, setColumnDefs])

  const handleDeleteColumn = useCallback(
    (index: number) => {
      setColumnDefs?.((prevColumns) =>
        (prevColumns ?? []).filter((_, i) => i !== index)
      )
    },
    [setColumnDefs]
  )

  const getHandleUpdateColumn: (
    idx: number
  ) => Dispatch<SetStateAction<DrilldownTableColumnDef>> = useCallback(
    (colIdx: number) => (val: SetStateAction<DrilldownTableColumnDef>) => {
      setColumnDefs?.((prevColumns) => {
        const currentColumns = prevColumns ?? []
        const updatedColumn =
          typeof val === 'function' ? val(currentColumns[colIdx]) : val
        return currentColumns.map((col, idx) =>
          idx === colIdx ? updatedColumn : col
        )
      })
    },
    [setColumnDefs]
  )

  const handlePopoverVisibleChange = useCallback(
    (visible: boolean, index: number) => {
      setPopoverVisible(visible)
      setEditingIndex(visible ? index : null)
    },
    []
  )

  const handleEditClick = useCallback((index: number) => {
    setEditingIndex(index)
    setPopoverVisible(true)
  }, [])

  const handleDrop = useCallback(
    (dragIndex: number, dropIndex: number) => {
      if (dragIndex === dropIndex) {
        return
      }

      setColumnDefs?.((prevColumns) => {
        const nextColumns = [...(prevColumns ?? [])]
        const [draggedColumn] = nextColumns.splice(dragIndex, 1)
        if (!draggedColumn) {
          return prevColumns ?? []
        }
        nextColumns.splice(dropIndex, 0, draggedColumn)
        return nextColumns
      })
      setPopoverVisible(false)
      setEditingIndex(null)
    },
    [setColumnDefs]
  )

  return (
    <Flex vertical align='center'>
      <List
        bordered
        dataSource={[...columnDefs]}
        locale={{ emptyText: 'No columns.' }}
        style={{ width: '100%', maxWidth: '37.5rem' }}
        renderItem={(column, index) => (
          <ColumnListItem
            column={column}
            index={index}
            isEditing={editingIndex === index && popoverVisible}
            onEdit={() => handleEditClick(index)}
            onDelete={() => handleDeleteColumn(index)}
            onDrop={handleDrop}
            onUpdateColumn={getHandleUpdateColumn(index)}
            onPopoverVisibleChange={(visible) =>
              handlePopoverVisibleChange(visible, index)
            }
          />
        )}
      />

      {setColumnDefs && (
        <Space
          style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}>
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={handleAddColumn}>
            Add Column
          </Button>
        </Space>
      )}
    </Flex>
  )
}
